import { useEffect, useState } from "react";
import BrandShell from "../components/BrandShell";
import SEO from "../components/SEO";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Trophy, RefreshCcw, Trash2, Save } from "lucide-react";

const FIELDS = [["brand_name", "Brand"], ["brand_niche", "Brand niche"], ["brand_city", "City"], ["creator_name", "Creator"], ["creator_niche", "Creator niche"], ["creator_followers", "Followers"], ["deal_type", "Deal type"], ["amount", "£ amount"], ["rating", "Rating"], ["review", "Review quote"]];

function Row({ row, onSaved, onDeleted }) {
  const [form, setForm] = useState(row);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(row);
  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/showcase/${row.id}`, patch);
      setForm(data); onSaved(data);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  const savePatch = () => {
    const patch = {};
    FIELDS.forEach(([k]) => { if (form[k] !== row[k]) patch[k] = ["creator_followers"].includes(k) ? parseInt(form[k]) || 0 : ["amount", "rating"].includes(k) ? parseFloat(form[k]) || 0 : form[k]; });
    save(patch);
  };
  return (
    <div className={`glass rounded-2xl p-4 space-y-3 ${row.hidden ? "opacity-50" : ""}`} data-testid={`showcase-row-${row.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-semibold text-white">{row.brand_name} <span className="text-zinc-500 font-normal">×</span> {row.creator_name} <span className="text-zinc-500 font-normal">· £{row.amount}</span></div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-zinc-400">Visible <Switch checked={!row.hidden} onCheckedChange={(v) => save({ hidden: !v })} data-testid={`showcase-visible-${row.id}`} /></label>
          <button onClick={async () => { if (!window.confirm("Delete this showcase deal?")) return; await api.delete(`/admin/showcase/${row.id}`); onDeleted(row.id); }} className="text-zinc-500 hover:text-red-300" data-testid={`showcase-delete-${row.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
        {FIELDS.map(([k, l]) => (
          <div key={k} className={k === "review" ? "sm:col-span-2 lg:col-span-5" : ""}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{l}</div>
            <Input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-8 text-xs bg-white/[0.02] border-white/10 text-white" data-testid={`showcase-field-${k}-${row.id}`} />
          </div>
        ))}
      </div>
      {dirty && <Button onClick={savePatch} disabled={busy} size="sm" className="rounded-full nexus-gradient text-white border-0" data-testid={`showcase-save-${row.id}`}><Save className="h-3.5 w-3.5 mr-1.5" /> Save changes</Button>}
    </div>
  );
}

export default function AdminShowcase() {
  const [data, setData] = useState(null);
  const load = async () => { try { const r = await api.get("/admin/showcase"); setData(r.data); } catch (e) { toast.error("Failed to load"); } };
  useEffect(() => { load(); }, []);

  const reset = async () => {
    if (!window.confirm("Regenerate all 282 showcase deals and their matching brand/creator profiles? Your edits will be lost.")) return;
    await api.post("/admin/showcase/reset"); toast.success("Regenerated deals + profiles"); load();
  };

  return (
    <BrandShell>
      <SEO title="Showcase deals · Admin" description="Manage the public results wall." path="/admin/showcase" />
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-8 space-y-6" data-testid="admin-showcase-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2"><Trophy className="h-3.5 w-3.5 text-nexus-pink" /> Admin</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">Results wall</h1>
            <p className="text-zinc-400 mt-2 text-sm">The public wall always shows real completed deals first, then fills to {data?.floor || 82} with these showcase deals. They never touch wallets, levels or ratings.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={load} variant="outline" className="rounded-full border-white/10 text-white hover:bg-white/5" data-testid="showcase-refresh-btn"><RefreshCcw className="h-4 w-4 mr-2" /> Refresh</Button>
            <Button onClick={reset} variant="outline" className="rounded-full border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid="showcase-reset-btn">Regenerate all</Button>
          </div>
        </div>
        {data && (
          <div className="grid grid-cols-3 gap-3" data-testid="showcase-stats">
            {[["Real completed deals", data.real_completed], ["Showcase currently shown", data.showing], ["Wall floor", data.floor]].map(([l, v]) => (
              <div key={l} className="glass rounded-2xl p-4"><div className="font-display font-bold text-2xl text-white">{v}</div><div className="text-[11px] uppercase tracking-widest text-zinc-500">{l}</div></div>
            ))}
          </div>
        )}
        {!data ? <div className="text-zinc-500">Loading…</div> : (
          <div className="space-y-3" data-testid="showcase-list">
            {data.rows.map((r) => <Row key={r.id} row={r} onSaved={(u) => setData((d) => ({ ...d, rows: d.rows.map((x) => (x.id === u.id ? u : x)) }))} onDeleted={(id) => setData((d) => ({ ...d, rows: d.rows.filter((x) => x.id !== id) }))} />)}
          </div>
        )}
      </div>
    </BrandShell>
  );
}
